import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";

const FastCard = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [isSigned, setIsSigned] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null);
  const [expiryDate, setExpiryDate] = useState("");
  const [fcNumber, setFcNumber] = useState("");
  const [createdDate, setCreatedDate] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // 🔍 Fetch FastCard info
  const fetchFastCard = useCallback(async () => {
    if (!userId) return;

    try {
      const res = await axios.get(`${API_BASE}/api/fastcard/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const data = res.data;

      // Always set these:
      setFcNumber(data.fc_number || "");
      setExpiryDate(
        data.fc_expiry_date
          ? new Date(data.fc_expiry_date).toISOString().split("T")[0]
          : ""
      );

      // Handle file info separately:
      if (data.fc_file) {
        setUploadedFileName(data.fc_file);
        setOriginalFileName(data.fc_original || data.fc_file);
        setCreatedDate(data.fc_created_date);
      } else {
        setUploadedFileName(null);
        setOriginalFileName(null);
        setCreatedDate(null);
      }
    } catch (err) {
      console.error("Fetch failed", err);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    fetchFastCard();
  }, [fetchFastCard]);

  // 🔍 Check Signature Status
  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const TASK_ID = 37; // ✅ Fast Card task ID
        const idKey = String(TASK_ID);
        const nameKey = "fast_card";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("Error checking signature status:", err);
      }
    };

    if (userId) fetchTaskStatus();
  }, [userId, API_BASE]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected && selected.type === "application/pdf") {
      setFile(selected);
    } else {
      alert("Only PDF files are allowed!");
      setFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("fc_expiry_date", expiryDate);
    formData.append("fc_number", fcNumber);
    if (file) formData.append("file", file);

    try {
      const response = await axios.post(
        `${API_BASE}/api/fastcard/upload?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const { filename, originalName, message } = response.data;
      if (filename) {
        setUploadedFileName(filename);
        setOriginalFileName(originalName || filename);
      }

      alert(message || "Saved successfully!");
      fetchFastCard();
    } catch (error) {
      console.error("Upload error:", error);
      alert(error.response?.data?.message || "Save failed. Please try again.");
    }
  };

  return (
    <>
      {/* 🔝 Header Section */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>Fast Card</span>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(-1)}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-4 text-start">
            {/* Left side can stay empty or hold something else */}
          </div>

          <div className="col-4 text-center">
            <h3 className="mb-0">Fast Card</h3>
          </div>

          <div className="col-4 text-end">
            <div>New‑Way Trucking Ltd</div>
            <div>7725 48 St. SE, Calgary</div>
            <div>Alberta, Canada T2C 2V3</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-3">
          <div className="row">
            <div className="col-md-4 mb-3">
              <label>Fast Card Number *</label>
              <input
                type="text"
                className="form-control"
                value={fcNumber}
                onChange={(e) => setFcNumber(e.target.value)}
                disabled={!isAdmin && isSigned}
                required
              />
            </div>
            <div className="col-md-4 mb-3">
              <label>Expiry Date *</label>
              <input
                type="date"
                className="form-control"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                disabled={!isAdmin && isSigned}
                required
              />
            </div>

            <div className="col-md-12 mb-3">
              <label>Select PDF file *</label>
              <input
                type="file"
                className="form-control"
                accept="application/pdf"
                onChange={handleFileChange}
                disabled={!isAdmin && isSigned}
              />
            </div>
          </div>

          {uploadedFileName && originalFileName && (
            <div className="p-3 bg-light rounded border">
              <div className="text-muted mb-2">Uploaded Version</div>
              <a
                href={`${API_BASE}/uploads/fastcards/${uploadedFileName}`}
                target="_blank"
                rel="noopener noreferrer"
                download={originalFileName}
              >
                {originalFileName}
              </a>
              {createdDate && (
                <div className="text-muted">
                  Uploaded on: {new Date(createdDate).toLocaleString()}
                </div>
              )}
            </div>
          )}

          <div className="mt-4 d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isAdmin && isSigned}
            >
              Save
            </button>
          </div>
        </form>

        <hr className="my-5" />

        {/* ✍ Signature + Review Section */}
        {userId && (
          <>
            <SignatureManager
              taskName="fast_card"
              taskId={37}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="fast_card"
              taskId={37}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default FastCard;
